import { Component, ElementRef, NgZone, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { EditorModule } from '@tinymce/tinymce-angular';
import { HeaderComponent } from '../header/header.component';
import { AuthService } from '../auth.service';
import { AllapiService } from '../allapi.service';
import { TransliterateService } from '../transliterate.service';
import { LanguageService } from '../language.service';
import { Subscription } from 'rxjs';

declare var tinymce: any;

@Component({
  selector: 'app-create',
  standalone: true,
  imports: [CommonModule, HeaderComponent, ReactiveFormsModule, EditorModule],
  templateUrl: './create.component.html',
  styleUrl: './create.component.css'
})
export class CreateComponent implements OnInit, OnDestroy {
  storyForm!: FormGroup;
  storyId: number = 0;
  selectedAudioFile: File | null = null;
  successMessage: string = '';
  errorMessage: string = '';
  message: string = '';
  currentLang: string = 'en';
  isLoading: boolean = true;
  editorInstance: any;

  @ViewChild('titleInput') titleInput!: ElementRef<HTMLInputElement>;

titleSuggestions: string[] = [];
showTitleSuggestions: boolean = false;
selectedTitleIndex: number = 0;
titleSuggestionBox!: HTMLDivElement;
titleDebounce: any = null;
  
  // Suggestion variables
   showSuggestions = false;
suggestions: string[] = [];

  suggestionBox: any;

currentSuggestions: string[] = [];
selectedSuggestionIndex: number = 0;

suggestionDropdown: HTMLDivElement | null = null;
  
  // Current typing context
  private currentWord: string = '';
  private textBeforeCursor: string = '';
  private textAfterCursor: string = '';
  private wordStartPosition: number = 0;
  
  private languageSubscription!: Subscription;
  private transliterationTimeout: any;

  private bufferStartPos = 0;
private bufferText = '';
private transliterationDebounce: any = null;
private MAX_BUFFER = 40; 

  videoFile: File | null = null;
  videoUploadProgress: number | null = null;
  uploadedVideoUrl: string = '';
  videoUploading: boolean = false;

  @ViewChild('audioInput') audioInput!: ElementRef<HTMLInputElement>;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    public router: Router,
    private http: HttpClient,
    private authService: AuthService,
    private contentService: AllapiService,
    private transliterateService: TransliterateService,
    private languageService: LanguageService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    const paramId = this.route.snapshot.paramMap.get('storyId');
    this.storyId = paramId ? Number(paramId) : 0;

    this.storyForm = this.fb.group({
      story_title: ['', Validators.required],
      story_content: ['', Validators.required],
      video_link: [''],
      audio_link: ['']
    });

    // Subscribe to language changes from header
    this.languageSubscription = this.languageService.currentLanguage$.subscribe(lang => {
      this.currentLang = lang;
      this.hideSuggestions();
    });

    // Get initial language
    this.currentLang = this.languageService.getCurrentLanguage();
  }

  ngOnDestroy(): void {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    this.removeSuggestionBox();
  }

ngAfterViewInit() {
  this.createTitleSuggestionBox();
}

private createTitleSuggestionBox() {
  this.titleSuggestionBox = document.createElement('div');
  this.titleSuggestionBox.className = 'title-suggestion-box';
  this.titleSuggestionBox.style.cssText = `
    position: absolute;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    z-index: 20000;
    display: none;
    max-width: 350px;
    padding: 5px;
    max-height: 160px;
    overflow-y: auto;
    font-size: 16px;
    color:#000
  `;
  document.body.appendChild(this.titleSuggestionBox);
}

onTitleInput(event: any) {
  if (this.currentLang === 'en') {
    this.hideTitleSuggestions();
    return;
  }

  const inputEl = this.titleInput.nativeElement;
  const value = inputEl.value;
  const cursorPos = inputEl.selectionStart || 0;

  // extract last word typed
  const match = value.substring(0, cursorPos).match(/([a-zA-Z0-9]+)$/);
  if (!match) {
    this.hideTitleSuggestions();
    return;
  }

  const buffer = match[1];

  clearTimeout(this.titleDebounce);
  this.titleDebounce = setTimeout(() => {
    this.transliterateService.transliterateText(buffer, this.currentLang)
      .subscribe({
        next: (res: any) => {
          if (res.success && res.suggestions && res.suggestions.length > 0) {
            this.titleSuggestions = res.suggestions;
            this.selectedTitleIndex = 0;
            this.showTitleSuggestionDropdown();
          } else {
            this.hideTitleSuggestions();
          }
        },
        error: () => this.hideTitleSuggestions()
      });
  }, 120);
}

private showTitleSuggestionDropdown() {
  const inputEl = this.titleInput.nativeElement;
  const rect = inputEl.getBoundingClientRect();

  this.titleSuggestionBox.style.left = `${rect.left}px`;
  this.titleSuggestionBox.style.top = `${rect.bottom + 5}px`;
  this.titleSuggestionBox.style.width = `${rect.width}px`;
  this.titleSuggestionBox.style.display = 'block';

  this.updateTitleSuggestionDisplay();
}

private updateTitleSuggestionDisplay() {
  this.titleSuggestionBox.innerHTML = '';

  this.titleSuggestions.forEach((sug, index) => {
    const div = document.createElement('div');
    div.textContent = sug;
    div.style.padding = "6px 10px";
    div.style.cursor = "pointer";
    div.style.background = index === this.selectedTitleIndex ? '#f0f0f0' : 'white';

    div.onclick = () => this.applyTitleSuggestion(sug);

    this.titleSuggestionBox.appendChild(div);
  });

  this.showTitleSuggestions = true;
}

onTitleKeydown(e: KeyboardEvent) {
  if (!this.showTitleSuggestions) return;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    this.selectedTitleIndex =
      (this.selectedTitleIndex + 1) % this.titleSuggestions.length;
    this.updateTitleSuggestionDisplay();
  }

  else if (e.key === 'ArrowUp') {
    e.preventDefault();
    this.selectedTitleIndex =
      (this.selectedTitleIndex - 1 + this.titleSuggestions.length) %
      this.titleSuggestions.length;
    this.updateTitleSuggestionDisplay();
  }

  else if (e.key === 'Enter') {
    e.preventDefault();
    this.applyTitleSuggestion(this.titleSuggestions[this.selectedTitleIndex]);
  }
}

private applyTitleSuggestion(word: string) {
  const inputEl = this.titleInput.nativeElement;
  const value = inputEl.value;

  const cursorPos = inputEl.selectionStart || 0;
  const beforeCursor = value.substring(0, cursorPos);

  const match = beforeCursor.match(/([a-zA-Z0-9]+)$/);
  if (!match) return;

  const buffer = match[1];
  const start = beforeCursor.lastIndexOf(buffer);

  const newValue =
    value.substring(0, start) +
    word + " " +
    value.substring(cursorPos);

  inputEl.value = newValue;
  this.storyForm.get('story_title')?.setValue(newValue);

  this.hideTitleSuggestions();
}

private hideTitleSuggestions() {
  this.showTitleSuggestions = false;
  if (this.titleSuggestionBox) {
    this.titleSuggestionBox.style.display = 'none';
  }
}
  private createSuggestionDropdown(): void {
  if (this.suggestionDropdown) return;

  const el = document.createElement('div');
  el.className = 'suggestion-dropdown';
  el.style.cssText = `
    position: absolute;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    z-index: 10000;
    display: none;
    max-width: 300px;
    max-height: 150px;
    overflow-y: auto;
  `;

  // append to editor body, not document.body
  const editorBody = this.editorInstance.getBody();
  editorBody.parentElement!.appendChild(el);

  this.suggestionDropdown = el;
}


private showSuggestionDropdownAtRect(rect: DOMRect): void {
  if (!this.suggestionDropdown) return;

  const editorContainer = this.editorInstance.getContentAreaContainer();
  const editorRect = editorContainer.getBoundingClientRect();

  // calculate relative position
  const left = rect.left - editorRect.left;
  const top = rect.bottom - editorRect.top;

  this.suggestionDropdown.style.left = `${left}px`;
  this.suggestionDropdown.style.top = `${top + window.scrollY}px`;
  this.suggestionDropdown.style.display = 'block';
  this.showSuggestions = true;

  this.updateSuggestionDisplay();
}

private showSuggestionDropdownAtCursor(): void {
  if (!this.suggestionDropdown || !this.editorInstance) return;

  const sel = this.editorInstance.selection;
  const rng = sel.getRng();
  if (!rng.collapsed) return;

  const editorBody = this.editorInstance.getBody();
  const rect = rng.getBoundingClientRect(); // caret pos inside iframe

  // Move popup DOWN by 40px
  const left = rect.left - editorBody.getBoundingClientRect().left;
  const top = rect.bottom - editorBody.getBoundingClientRect().top + 40;

  this.suggestionDropdown.style.left = `${left}px`;
  this.suggestionDropdown.style.top = `${top}px`;
  this.suggestionDropdown.style.display = 'block';
  this.showSuggestions = true;

  this.updateSuggestionDisplay();
}




  onEditorInit(event: any): void {
  this.editorInstance = event.editor;
  this.createSuggestionDropdown();

this.editorInstance.on('keydown', (event: KeyboardEvent) => {
  this.zone.run(() => {
    this.handleAutoConvert(event);
  });
});

  this.editorInstance.on('input', () => {
    this.zone.run(() => this.handleEditorInput());
  });

  this.editorInstance.on('click', () => {
    this.zone.run(() => this.hideSuggestions());
  });

  // When editor loses focus hide
  this.editorInstance.on('blur', () => {
    this.zone.run(() => this.hideSuggestions());
  });
}

  // Create suggestion box element
  private createSuggestionBox(): void {
    this.suggestionBox = document.createElement('div');
    this.suggestionBox.className = 'suggestion-box';
    this.suggestionBox.style.cssText = `
      position: absolute;
      background: white;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      z-index: 10000;
      display: none;
      max-width: 300px;
      max-height: 150px;
      overflow-y: auto;
    `;
    document.body.appendChild(this.suggestionBox);
  }

  // Remove suggestion box
  private removeSuggestionBox(): void {
    if (this.suggestionBox && this.suggestionBox.parentNode) {
      this.suggestionBox.parentNode.removeChild(this.suggestionBox);
    }
  }

  // Handle editor keydown events
private handleEditorKeydown(e: KeyboardEvent): void {
  if (!this.showSuggestions) return;

  if (e.key === 'ArrowDown') {
    this.selectedSuggestionIndex =
      (this.selectedSuggestionIndex + 1) % this.currentSuggestions.length;
    this.updateSuggestionDisplay();
  }

  else if (e.key === 'ArrowUp') {
    this.selectedSuggestionIndex =
      (this.selectedSuggestionIndex - 1 + this.currentSuggestions.length) %
      this.currentSuggestions.length;
    this.updateSuggestionDisplay();
  }

  else if (e.key === 'Enter') {
    e.preventDefault();
    this.applySuggestion(this.currentSuggestions[this.selectedSuggestionIndex]);
  }
}

private applySuggestion(word: string): void {
  const editor = this.editorInstance;
  if (!editor) return;

  editor.execCommand('mceInsertContent', false, word + ' ');
  this.hideSuggestionDropdown();
}
private hideSuggestionDropdown(): void {
  this.showSuggestions = false;
  if (this.suggestionDropdown) {
    this.suggestionDropdown.style.display = 'none';
  }
}
private replaceBufferWith(converted: string): void {
  const sel = this.editorInstance.selection;
  const rng = sel.getRng();
  const node = rng.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return;

  const fullText = node.textContent || '';

  const before = fullText.substring(0, this.bufferStartPos);
  const after = fullText.substring(this.bufferStartPos + this.bufferText.length);
  const newText = before + converted + after;

  node.textContent = newText;

  // set caret after converted word
  const newCursor = this.bufferStartPos + converted.length;
  const newRange = document.createRange();
  newRange.setStart(node, newCursor);
  newRange.setEnd(node, newCursor);
  this.editorInstance.selection.setRng(newRange);

  // register undo
  try { this.editorInstance.undoManager.add(); } catch (err) { /* ignore */ }

  // cleanup
  this.hideSuggestions();
}

private selectSuggestionByIndex(index: number, insertSpace: boolean = false): void {
  if (index < 0 || index >= this.suggestions.length) return;
  const selected = this.suggestions[index];
  this.replaceBufferWith(selected);
  if (insertSpace) {
    setTimeout(() => this.insertTextAtCursor(' '), 10);
  }
}
  // Handle editor input for suggestions
private handleEditorInput(): void {
  if (this.currentLang === 'en') {
    this.hideSuggestions();
    return;
  }

  const sel = this.editorInstance.selection;
  const rng = sel.getRng();
  if (!rng.collapsed) {
    this.hideSuggestions();
    return;
  }

  const node = rng.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) {
    this.hideSuggestions();
    return;
  }

  const fullText = node.textContent || '';
  const cursorOffset = rng.startOffset;

  // compute buffer start - continuous English letters/digits only
  const start = this.findBufferStart(fullText, cursorOffset);
  const buffer = fullText.substring(start, cursorOffset);

  // if buffer empty or too long, hide
  if (!buffer || buffer.length === 0 || buffer.length > this.MAX_BUFFER || !/^[a-zA-Z0-9]+$/.test(buffer)) {
    this.hideSuggestions();
    return;
  }

  // store buffer info for replacement later
  this.bufferStartPos = start;
  this.bufferText = buffer;

  // debounced transliteration request
  clearTimeout(this.transliterationDebounce);
  this.transliterationDebounce = setTimeout(() => {
    this.requestSuggestions(buffer);
  }, 150);
}

private findBufferStart(text: string, cursor: number): number {
  let i = cursor - 1;
  while (i >= 0 && /[a-zA-Z0-9]/.test(text[i])) i--;
  return i + 1;
}
private requestSuggestions(buffer: string): void {
  // ensure buffer still matches current editor content at expected position
  const sel = this.editorInstance.selection;
  const rng = sel.getRng();
  if (!rng.collapsed) {
    this.hideSuggestions();
    return;
  }
  const node = rng.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) { this.hideSuggestions(); return; }
  const fullText = node.textContent || '';
  const currentBuffer = fullText.substring(this.bufferStartPos, rng.startOffset);
  if (currentBuffer !== buffer) {
    // buffer changed meanwhile
    return;
  }

  // call transliterateService
  this.transliterateService.transliterateText(buffer, this.currentLang).subscribe({
    next: (res: any) => {
      if (res && res.success && Array.isArray(res.suggestions) && res.suggestions.length > 0) {
        this.suggestions = res.suggestions;
        this.selectedSuggestionIndex = 0;

        // position dropdown under caret
        const bounding = rng.getBoundingClientRect();
        // this.showSuggestionDropdownAtRect(bounding);
        this.showSuggestionDropdownAtCursor();

      } else {
        this.hideSuggestions();
      }
    },
    error: (err) => {
      console.error('Transliterate error', err);
      this.hideSuggestions();
    }
  });
}
private liveTransliterate(buffer: string, node: any, fullText: string, start: number, cursor: number): void {
  this.transliterateService.transliterateText(buffer, this.currentLang)
    .subscribe({
      next: (res) => {
        if (!res.success || !res.suggestions?.length) return;
        
        const converted = res.suggestions[0];

        const newText = 
          fullText.substring(0, start) + 
          converted + 
          fullText.substring(cursor);

        node.textContent = newText;

        // Adjust caret after live update
        const range = document.createRange();
        range.setStart(node, start + converted.length);
        range.setEnd(node, start + converted.length);
        this.editorInstance.selection.setRng(range);
      }
    });
}

  // Extract current word being typed
  private extractCurrentWord(fullText: string, cursorOffset: number): void {
    let wordStart = cursorOffset - 1;
    let wordEnd = cursorOffset;
    
    // Find start of word
    while (wordStart >= 0 && !/\s/.test(fullText[wordStart])) {
      wordStart--;
    }
    wordStart++;
    
    // Find end of word
    while (wordEnd < fullText.length && !/\s/.test(fullText[wordEnd])) {
      wordEnd++;
    }
    
    this.currentWord = fullText.substring(wordStart, wordEnd);
    this.textBeforeCursor = fullText.substring(0, wordStart);
    this.textAfterCursor = fullText.substring(wordEnd);
    this.wordStartPosition = wordStart;
  }

  // Get transliteration suggestions
  private getSuggestions(word: string): void {
    this.transliterateService.transliterateText(word, this.currentLang)
      .subscribe({
        next: (result) => {
          if (result.success && result.suggestions && result.suggestions.length > 0) {
            this.suggestions = result.suggestions;
            this.selectedSuggestionIndex = 0;
            this.showSuggestionBox();
          } else {
            this.hideSuggestions();
          }
        },
        error: (error) => {
          console.error('Suggestion error:', error);
          this.hideSuggestions();
        }
      });
  }

  // Show suggestion box
  private showSuggestionBox(): void {
    if (!this.suggestionBox) return;

    const selection = this.editorInstance.selection;
    const rng = selection.getRng();
    const rect = rng.getBoundingClientRect();
    
    this.suggestionBox.style.display = 'block';
    this.suggestionBox.style.left = rect.left + 'px';
    this.suggestionBox.style.top = (rect.bottom + window.scrollY) + 'px';
    
    this.updateSuggestionDisplay();
    this.showSuggestions = true;
  }

  // Update suggestion box content
 private updateSuggestionDisplay(): void {
  if (!this.suggestionDropdown) return;
  this.suggestionDropdown.innerHTML = '';
  if (!this.suggestions || this.suggestions.length === 0) {
    this.hideSuggestions();
    return;
  }

  this.suggestions.forEach((sugg, idx) => {
    const div = document.createElement('div');
    div.className = 'suggestion-item' + (idx === this.selectedSuggestionIndex ? ' active' : '');
    div.textContent = sugg;
    div.addEventListener('mousedown', (ev) => {
      // use mousedown to avoid losing selection before click
      ev.preventDefault();
      this.selectSuggestionByIndex(idx);
    });
    this.suggestionDropdown!.appendChild(div);
  });
}

  // Hide suggestions
 private hideSuggestions(): void {
  if (this.suggestionDropdown) {
    this.suggestionDropdown.style.display = 'none';
  }
  this.showSuggestions = false;
  this.suggestions = [];
  this.selectedSuggestionIndex = -1;
  this.bufferText = '';
}

  // Select current highlighted suggestion
  private selectCurrentSuggestion(): void {
    if (this.selectedSuggestionIndex >= 0 && this.suggestions[this.selectedSuggestionIndex]) {
      this.selectSuggestion(this.suggestions[this.selectedSuggestionIndex]);
    }
  }

  // Select a suggestion
  private selectSuggestion(suggestion: string): void {
    const selection = this.editorInstance.selection;
    const rng = selection.getRng();
    const node = rng.startContainer;
    
    if (node.nodeType === Node.TEXT_NODE) {
      const fullText = node.textContent || '';
      
      const newText = this.textBeforeCursor + suggestion + this.textAfterCursor;
      node.textContent = newText;
      
      // Set cursor position after the inserted word
      const newCursorPosition = this.wordStartPosition + suggestion.length;
      const newRange = document.createRange();
      newRange.setStart(node, newCursorPosition);
      newRange.setEnd(node, newCursorPosition);
      selection.setRng(newRange);
      
      this.editorInstance.undoManager.add();
    }
    
    this.hideSuggestions();
  }

  // Handle auto-convert on space/enter
private handleAutoConvert(e: KeyboardEvent): void {
  if (this.currentLang === 'en') return;  // English में auto convert नहीं

  // अगर key space, enter, period पर हो तभी convert
  if (![' ', 'Enter', '.', ',', '?'].includes(e.key)) return;

  const sel = this.editorInstance.selection;
  const rng = sel.getRng();

  if (!rng.collapsed) return;

  const node = rng.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return;

  const fullText = node.textContent || '';
  const cursor = rng.startOffset;

  // word start खोजो
  let start = cursor - 1;
  while (start >= 0 && /[a-zA-Z0-9]/.test(fullText[start])) start--;
  start++;

  const buffer = fullText.substring(start, cursor);

  // English word नहीं है तो return
  if (!buffer || !/^[a-zA-Z0-9]+$/.test(buffer)) return;

  // API call करके auto convert
  this.transliterateService.transliterateText(buffer, this.currentLang)
    .subscribe({
      next: (res) => {
        if (!res.success || !res.suggestions?.length) return;

        const converted = res.suggestions[0]; // Top suggestion auto apply

        const newText =
          fullText.substring(0, start) +
          converted +
          fullText.substring(cursor);

        node.textContent = newText;

        // caret को सही जगह set करो
        const newCursor = start + converted.length;
        const newRange = document.createRange();
        newRange.setStart(node, newCursor);
        newRange.setEnd(node, newCursor);
        sel.setRng(newRange);

        // user की space key continue रहने दो (space insert manually)
        if (e.key === ' ') {
          this.insertTextAtCursor(' ');
        }
      }
    });
}

  // Replace word in editor
  private replaceWordInEditor(node: Node, convertedWord: string, key: string): void {
    const newText = this.textBeforeCursor + convertedWord + 
                   (key === 'Enter' ? '\n' : ' ') + this.textAfterCursor;
    
    node.textContent = newText;
    
    const newCursorPosition = this.wordStartPosition + convertedWord.length + 1;
    const newRange = document.createRange();
    newRange.setStart(node, newCursorPosition);
    newRange.setEnd(node, newCursorPosition);
    
    this.editorInstance.selection.setRng(newRange);
    this.editorInstance.undoManager.add();
  }

  // Insert text at cursor
  private insertTextAtCursor(text: string): void {
  this.editorInstance.execCommand('mceInsertContent', false, text);
}

  // Check if word is English
  private isEnglishWord(word: string): boolean {
    return /^[a-zA-Z]+$/.test(word);
  }

  // Rest of your existing methods remain the same...
  onAudioFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      this.selectedAudioFile = file;
    }
  }

  onVideoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) return;

    const maxMB = 200;
    if (file.size > maxMB * 1024 * 1024) {
      alert('Video too large. Max ' + maxMB + 'MB.');
      return;
    }

    this.videoFile = file;
    this.videoUploadProgress = 0;
    this.videoUploading = true;

    this.uploadVideoFile();
  }

  uploadVideoFile() {
    if (!this.videoFile) return;

    const fd = new FormData();
    fd.append('video', this.videoFile, this.videoFile.name);

    this.http.post('https://stories.funtelstudio.com/storyapi/upload_video.php', fd, {
      reportProgress: true,
      observe: 'events'
    }).subscribe({
      next: (event: any) => {
        if (event.type === 1 && event.total) { 
          this.videoUploadProgress = Math.round(100 * event.loaded / event.total);
        } 
        else if (event.type === 4) { 
          this.videoUploading = false;
          if (event.body && event.body.status === 'success') {
            this.uploadedVideoUrl = event.body.url;
            this.videoUploadProgress = 100;
            this.storyForm.patchValue({ video_link: event.body.url });
          } else {
            alert('Upload failed: ' + (event.body?.message || 'Unknown'));
            this.videoUploadProgress = null;
          }
        }
      },
      error: err => {
        this.videoUploading = false;
        console.error('Upload error', err);
        alert('Upload failed.');
        this.videoUploadProgress = null;
      }
    });
  }

  triggerFileInput(): void {
    this.audioInput.nativeElement.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files[0];
    if (file && (file.type === 'audio/mp3' || file.type === 'audio/wav')) {
      this.selectedAudioFile = file;
    } else {
      alert("Only MP3 or WAV files are allowed.");
    }
  }

  onSubmit(isPublish: boolean = false): void {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      alert('You must be logged in to save a story.');
      this.router.navigate(['login']);
      return;
    }

    if (this.storyForm.invalid) {
      alert('Please complete all required fields');
      return;
    }

    const formValues = this.storyForm.value;
    const formData = new FormData();
    formData.append('user_id', userId.toString());
    formData.append('story_title', formValues.story_title);
    formData.append('story_content', formValues.story_content);
    formData.append('story_id', this.storyId.toString());
    formData.append('video_link', formValues.video_link || '');
    formData.append('status', isPublish ? '1' : '0');
    formData.append('audio_link', formValues.audio_link || '');

    this.http.post('https://stories.funtelstudio.com/storyapi/addstory.php', formData).subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          this.successMessage = isPublish ? '✓ Story Published!' : '✓ Draft Saved!';
          this.errorMessage = '';
          setTimeout(() => {
            this.successMessage = '';
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.router.navigate(['/episode', `${this.storyId}-` + this.slugify(formValues.story_title)]);
          }, 2000);
        } else {
          this.errorMessage = '⚠ Error: ' + res.message;
          this.successMessage = '';
        }
      },
      error: (err) => {
        console.error('⚠ API error', err);
        alert('Something went wrong.');
      }
    });
  }

  publishStory(): void {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      this.errorMessage = '⚠ You must be logged in to publish a story';
      this.router.navigate(['/login']);
      return;
    }

    if (this.storyForm.invalid) {
      this.markFormGroupTouched(this.storyForm);
      this.errorMessage = '⚠ Please complete all required fields';
      return;
    }

    const formValues = this.storyForm.value;
    const formData = new FormData();

    formData.append('user_id', userId.toString());
    formData.append('story_title', formValues.story_title.trim());
    formData.append('story_content', formValues.story_content.trim());
    formData.append('story_id', this.storyId.toString());
    formData.append('status', '1');

    if (formValues.video_link?.trim()) {
      formData.append('video_link', formValues.video_link.trim());
    }

    if (formValues.audio_link?.trim()) {
      formData.append('audio_link', formValues.audio_link.trim());
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.http.post('https://stories.funtelstudio.com/storyapi/addstory.php', formData).subscribe({
      next: (res: any) => {
        if (res.status !== 'success') {
          this.handleError('⚠ Error saving content: ' + res.message);
          return;
        }

        const publishData = new FormData();
        publishData.append('story_id', this.storyId.toString());
        publishData.append('story_title', formValues.story_title.trim());
        publishData.append('story_content', formValues.story_content.trim());

        if (formValues.audio_link?.trim()) {
          publishData.append('audio_link', formValues.audio_link.trim());
        }
        if (formValues.video_link?.trim()) {
          publishData.append('video_link', formValues.video_link.trim());
        }

        this.http.post('https://stories.funtelstudio.com/storyapi/publish_story.php', publishData).subscribe({
          next: (publishRes: any) => {
            this.isLoading = false;

            if (publishRes.status !== 'success') {
              this.handleError('⚠ Error publishing story: ' + publishRes.message);
              return;
            }

            this.successMessage = '✓ Story Published Successfully!';
            setTimeout(() => {
              this.router.navigate(['/episode', `${this.storyId}-${this.slugify(formValues.story_title)}`]);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 2000);
          },
          error: (publishErr) => {
            this.handleError('⚠ Error publishing story', publishErr);
          }
        });
      },
      error: (err) => {
        this.handleError('⚠ Error saving content', err);
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private handleError(message: string, error?: any) {
    this.isLoading = false;
    this.errorMessage = message;
    console.error(message, error);
    
    setTimeout(() => {
      if (this.errorMessage === message) {
        this.errorMessage = '';
      }
    }, 5000);
  }

  private stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  private slugify(text: string): string {
    return text.toLowerCase().trim().replace(/[\s\W-]+/g, '-').replace(/^-+|-+$/g, '');
  }
}